import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Link, useNavigate } from 'react-router-dom';
import XpnsrPageHeader from './XpnsrPageHeader';
import BRAND from '../brand';
import {
  ArrowUpRight,
  BarChart3,
  Plus,
  Target,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import useLiveStats from '../hooks/useLiveStats';
import FirstDayChecklist from './FirstDayChecklist';
import LiveClickFeed from './LiveClickFeed';
import { BACKEND_URL } from '../runtimeConfig';

// YYYY-MM-DD in the browser's local time (toISOString would give the UTC date).
function localIsoDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Longest custom period the API accepts (inclusive days).
const MAX_RANGE_DAYS = 366;

/* Money in the workspace currency; falls back to "$1.23" if Intl does not
   know the code. */
function money(value, currency = 'USD', digits = 2) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: digits === 3 ? 3 : 0,
      maximumFractionDigits: digits,
    }).format(n);
  } catch (_e) {
    return '$' + n.toFixed(digits);
  }
}

/* Small trend line for the campaigns table: green when the second half of
   the period beats the first, red when it falls, grey when flat. */
function Spark({ data }) {
  const w = 120;
  const h = 30;
  if (!data || data.length < 2) return <span className="xp-spark-none">—</span>;
  const max = Math.max(...data);
  const min = Math.min(...data);
  if (max === 0) {
    return (
      <svg className="xp-spark flat" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <path className="line" d={`M0 ${h - 3} H${w}`} />
      </svg>
    );
  }
  const span = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 3 - ((v - min) / span) * (h - 6)]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const half = Math.floor(data.length / 2);
  const first = data.slice(0, half).reduce((a, v) => a + v, 0);
  const second = data.slice(half).reduce((a, v) => a + v, 0);
  const dir = second > first ? 'up' : second < first ? 'down' : 'flat';
  return (
    <svg className={'xp-spark ' + dir} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path className="area" d={line + ` L${w} ${h} L0 ${h} Z`} />
      <path className="line" d={line} />
    </svg>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_clicks: 0,
    unique_clicks: 0,
    conversions: 0,
    revenue: 0,
    conversion_rate: 0,
  });
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [sources, setSources] = useState({});
  const [summary, setSummary] = useState({});
  // "Recent campaigns": campaigns with traffic in the selected period first, then
  // the newest. The list endpoint returns campaigns in creation order, so taking
  // the first eight showed only the oldest ones and hid every new campaign.
  const campaigns = useMemo(() => {
    const activity = (c) => {
      const s = summary[c.id];
      return s ? (Number(s.clicks) || 0) + (Number(s.conversions) || 0) : 0;
    };
    return [...allCampaigns]
      .sort((a, b) => activity(b) - activity(a) || String(b.created_at || '').localeCompare(String(a.created_at || '')))
      .slice(0, 8);
  }, [allCampaigns, summary]);
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  // 1 = today, 7/14/30 = last N days, 'all', or 'custom' (customRange).
  const [chartDays, setChartDays] = useState(7);
  const [customRange, setCustomRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return { from: localIsoDate(from), to: localIsoDate(to) };
  });
  const todayIso = localIsoDate(new Date());
  const rangeDays = customRange.from && customRange.to
    ? Math.round((new Date(customRange.to) - new Date(customRange.from)) / 86400000) + 1
    : 0;
  const customValid = rangeDays >= 1 && rangeDays <= MAX_RANGE_DAYS;
  const rangeKey = chartDays === 'custom' ? `${customRange.from}|${customRange.to}` : '';
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // The first-day checklist below replaced the old setup wizard (removed).

  // Wave 9 — keep counters live without full-page polling.
  const backendUrl = BACKEND_URL;
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : '';
  const { stats: liveStats, connected: liveConnected } = useLiveStats({ backendUrl, token, enabled: Boolean(token) });
  // Baseline for the live overlay. The headline cards are ALL-TIME totals
  // (from /api/dashboard/stats), while the WS frame reports only TODAY's
  // absolute counters. We remember the first live frame's "today" values plus
  // the all-time values they arrived on, then add only the *growth* since —
  // so the counters tick up live without the first frame overwriting the
  // all-time total with today's (smaller) count. Reset on every fetchStats.
  const liveBaselineRef = useRef(null);

  useEffect(() => {
    Promise.all([fetchStats(), fetchRecentCampaigns(), fetchChartData()]).then(() => setLastUpdated(new Date()));
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [chartDays, rangeKey]);

  useEffect(() => {
    axios
      .get(`${API}/traffic-sources`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((src) => {
          map[src.id] = src.name;
        });
        setSources(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [chartDays, rangeKey]);

  const trendSpan = typeof chartDays === 'number' ? Math.max(7, chartDays) : chartDays === 'all' ? 30 : 14;
  const recentIds = campaigns.map((c) => c.id).join(',');
  useEffect(() => {
    if (!recentIds) return undefined;
    let alive = true;
    Promise.all(
      recentIds.split(',').map((id) =>
        axios
          .get(`${API}/analytics/performance`, { params: { days: trendSpan, campaign_id: id } })
          .then((r) => [id, (r.data || []).map((d) => Number(d.clicks) || 0)])
          .catch(() => [id, []])
      )
    ).then((pairs) => {
      if (alive) setTrends(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
  }, [recentIds, trendSpan]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchStats(), fetchRecentCampaigns(), fetchChartData(), fetchSummary()]);
      setLastUpdated(new Date());
    } finally {
      setRefreshing(false);
    }
  };

  // Fresh numbers every minute while the tab is visible; the live feed moves
  // today's counters in between.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refreshAll();
    }, 60000);
    return () => clearInterval(id);
  }, [chartDays, rangeKey]);

  // Merge the WS snapshot into the polled values whenever it lands. The
  // /api/dashboard/stats response is the source of truth for the all-time
  // totals and for unique-clicks (which the WS payload doesn't expose yet).
  // The live frame carries only TODAY's absolute counters, so we add just
  // their growth since the first frame instead of replacing the all-time
  // totals — otherwise the initial frame (today=0) would clobber the
  // all-time value (e.g. drop 15 clicks to 0). See liveBaselineRef.
  useEffect(() => {
    if (!liveStats) return;
    const todayClicks = liveStats.total_clicks_today ?? 0;
    const todayConversions = liveStats.total_conversions_today ?? 0;
    setStats((prev) => {
      if (!liveBaselineRef.current) {
        // First live frame after a stats load — capture the baseline so later
        // frames add only the delta. Don't touch the all-time totals here.
        liveBaselineRef.current = {
          clicksToday: todayClicks,
          conversionsToday: todayConversions,
          clicksAllTime: prev.total_clicks ?? 0,
          conversionsAllTime: prev.conversions ?? 0,
        };
        return {
          ...prev,
          // active_campaigns matches the card's "active campaigns" label.
          active_campaigns: liveStats.active_campaigns ?? prev.active_campaigns,
        };
      }
      const base = liveBaselineRef.current;
      return {
        ...prev,
        total_clicks: base.clicksAllTime + Math.max(0, todayClicks - base.clicksToday),
        conversions: base.conversionsAllTime + Math.max(0, todayConversions - base.conversionsToday),
        active_campaigns: liveStats.active_campaigns ?? prev.active_campaigns,
      };
    });
  }, [liveStats]);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/dashboard/stats`);
      setStats(r.data);
      // Fresh all-time snapshot — re-baseline the live overlay so the next WS
      // frame measures its delta against these values, not stale ones.
      liveBaselineRef.current = null;
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  const fetchRecentCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/campaigns`);
      setAllCampaigns(r.data || []);
      setLoading(false);
    } catch (e) {
      console.error('Error fetching campaigns:', e);
      setLoading(false);
    }
  };

  // The period control drives every number on the page. "All time" keeps the
  // live all-time counters; the chart then shows the last 90 days.
  const isAll = chartDays === 'all';
  const isCustom = chartDays === 'custom';
  // Query params for the selected period, or null while a custom range is
  // incomplete. Presets ask the summary for whole local days (calendar=1) so
  // the cards cover exactly the days the chart shows.
  const periodParams = (forChart) => {
    if (isCustom) return customValid ? { date_from: customRange.from, date_to: customRange.to } : null;
    if (isAll) return { days: forChart ? 90 : 3650 };
    return forChart ? { days: chartDays } : { days: chartDays, calendar: 1 };
  };

  const fetchSummary = async () => {
    try {
      const params = periodParams(false);
      if (!params) return;
      const r = await axios.get(`${API}/analytics/campaigns-summary`, { params });
      const map = {};
      (r.data || []).forEach((row) => {
        map[row.campaign_id] = row;
      });
      setSummary(map);
    } catch (e) {
      console.error('Error fetching campaign summary:', e);
    }
  };

  const fetchChartData = async () => {
    try {
      const params = periodParams(true);
      if (!params) return;
      const r = await axios.get(`${API}/analytics/performance`, { params });
      setChartData(r.data || []);
    } catch (e) {
      console.error('Error fetching chart data:', e);
    }
  };

  const formatNumber = (num) => {
    const n = Number(num) || 0;
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  let periodLabel = t('Last {n} days', { n: chartDays });
  if (isAll) periodLabel = '';
  else if (isCustom) periodLabel = `${customRange.from} – ${customRange.to}`;
  else if (chartDays === 1) periodLabel = t('Today');
  const periodTotals = useMemo(() => {
    if (isAll) return null;
    const rows = Object.values(summary);
    const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const clicks = sum('clicks');
    const conversions = sum('conversions');
    return {
      total_clicks: clicks,
      conversions,
      revenue: sum('revenue'),
      cost: sum('cost'),
      unique_clicks: chartData.reduce((a, d) => a + (Number(d.unique_clicks) || 0), 0),
      conversion_rate: clicks > 0 ? (conversions / clicks) * 100 : 0,
    };
  }, [isAll, summary, chartData]);
  const shown = periodTotals ? { ...stats, ...periodTotals } : stats;

  const kpis = [
    {
      lbl: t('Campaigns'),
      // Active campaigns: the same number the live feed pushes every few seconds.
      n: shown.active_campaigns ?? shown.total_campaigns,
      desc: t('Total active campaigns'),
    },
    {
      lbl: t('Clicks'),
      primary: true,
      n: formatNumber(shown.total_clicks),
      desc: periodLabel || t('Total clicks'),
    },
    {
      lbl: t('Unique visitors'),
      n: formatNumber(shown.unique_clicks),
      desc: periodLabel || t('Unique clicks'),
    },
    {
      lbl: t('Conversions'),
      primary: true,
      n: shown.conversions,
      desc: t('{rate}% conversion', { rate: (Number(shown.conversion_rate) || 0).toFixed(1) }),
    },
    {
      lbl: t('Revenue'),
      primary: true,
      n: '$' + (Number(shown.revenue) || 0).toFixed(2),
      desc: periodLabel || t('Total revenue'),
    },
    {
      lbl: t('Cost'),
      n: '$' + (Number(shown.cost) || 0).toFixed(2),
      desc: periodLabel || t('Total spend'),
    },
    {
      lbl: t('Profit'),
      n: '$' + ((Number(shown.revenue) || 0) - (Number(shown.cost) || 0)).toFixed(2),
      desc: ((Number(shown.revenue) || 0) - (Number(shown.cost) || 0)) >= 0 ? t('Net positive') : t('Net loss'),
      color: ((Number(shown.revenue) || 0) - (Number(shown.cost) || 0)) >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))',
    },
    {
      lbl: t('EPC'),
      n: '$' + ((Number(shown.total_clicks) || 0) > 0 ? ((Number(shown.revenue) || 0) / (Number(shown.total_clicks) || 1)).toFixed(2) : '0.00'),
      desc: t('Earnings per click'),
    },
    {
      lbl: t('ROI'),
      primary: true,
      // ROI is undefined without spend — showing "0.0%" wrongly implies
      // break-even. Show an em dash when cost is 0.
      n: ((Number(shown.cost) || 0) > 0 ? (((Number(shown.revenue) || 0) - (Number(shown.cost) || 0)) / (Number(shown.cost) || 1) * 100).toFixed(1) + '%' : '—'),
      desc: t('Return on investment'),
    },
  ];

  return (
    <>
      <div style={{ padding: '0 32px' }}>
        <XpnsrPageHeader
          title={
            <span data-testid="dashboard-title">
              {t('Welcome,')} <em>{user?.full_name || t('User')}</em>.
            </span>
          }
          subtitle={
            <>
              {BRAND.terminalLabels ? '// TRACKER · ' : null}<b>{t('Real-time overview of your campaigns and stats')}</b>
              {liveConnected && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, fontSize: 10, color: 'rgb(var(--success))' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgb(var(--success))', animation: 'pulse 2s infinite' }} />
                  LIVE
                </span>
              )}
            </>
          }
          actions={
            <>
              <div className="xp-seg" role="group" aria-label={t('Period')} data-testid="dashboard-period">
                {[1, 7, 14, 30, 'all', 'custom'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setChartDays(d)}
                    className={chartDays === d ? 'is-on' : undefined}
                    aria-pressed={chartDays === d}
                  >
                    {d === 'all' ? t('All time') : d === 'custom' ? t('Custom range') : d === 1 ? t('Today') : d + 'd'}
                  </button>
                ))}
              </div>
              {isCustom && (
                <div className={'xp-range' + (customValid ? '' : ' is-invalid')} data-testid="dashboard-range">
                  <input
                    type="date"
                    aria-label={t('Period start')}
                    value={customRange.from}
                    max={customRange.to || todayIso}
                    onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
                  />
                  <span aria-hidden="true">–</span>
                  <input
                    type="date"
                    aria-label={t('Period end')}
                    value={customRange.to}
                    min={customRange.from || undefined}
                    max={todayIso}
                    onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={refreshAll}
                disabled={refreshing}
                className="xp-btn"
                data-testid="dashboard-refresh"
                title={lastUpdated ? t('Updated at {time}', { time: lastUpdated.toLocaleTimeString() }) : t('Refresh')}
              >
                <RefreshCw style={{ width: 14, height: 14, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                {t('Refresh')}
              </button>
              <Link to="/analytics" className="xp-btn">
                <BarChart3 style={{ width: 14, height: 14 }} />
                {t('Analytics')}
              </Link>
              <Link
                to="/campaigns"
                className="xp-btn xp-btn-primary"
                data-testid="create-campaign-button"
              >
                <Plus style={{ width: 14, height: 14 }} />
                {t('Create campaign')}
              </Link>
            </>
          }
        />
        <FirstDayChecklist />
      </div>

      {/* KPI: four headline numbers, the rest in a compact strip (same as the site) */}
      <div className="xp-kpis">
        {kpis.map((k, i) => ({ ...k, i })).filter((k) => k.primary).map((k) => (
          <div className="xp-kpi" key={k.i} data-testid={`stat-card-${k.i}`}>
            <div className="xp-kpi-lbl">{k.lbl}</div>
            <div className="xp-kpi-n" style={k.color ? { color: k.color } : undefined}>{k.n}</div>
            <div className="xp-kpi-dlt">{k.desc}</div>
          </div>
        ))}
      </div>
      <div className="xp-kpi-strip">
        {kpis.map((k, i) => ({ ...k, i })).filter((k) => !k.primary).map((k) => (
          <div className="xp-kpi-mini" key={k.i} data-testid={`stat-card-${k.i}`} title={k.desc}>
            <span>{k.lbl}</span>
            <b style={k.color ? { color: k.color } : undefined}>{k.n}</b>
          </div>
        ))}
      </div>

      {/* Chart and live clicks side by side, like the site mock */}
      <div className="xp-dash-grid">
        <Card className="xp-panel xp-dash-chart">
          <CardHeader className="xp-panel-h3" style={{ padding: '16px 22px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardTitle
              className="xp-panel-title"
            >
              <TrendingUp className="w-4 h-4 text-primary" />
              {t('Performance')}
            </CardTitle>
            <span className="xp-panel-sub">{isAll ? t('Last {n} days', { n: 90 }) : periodLabel}</span>
          </CardHeader>
          <CardContent style={{ padding: '12px 22px 22px' }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sClicks" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="rgb(var(--v2-glow, var(--tint)))" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="rgb(var(--tint))" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="rgb(var(--tint))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="rgb(var(--tint))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gConv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="rgb(var(--success))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="rgb(var(--success))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'rgb(var(--muted))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgb(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'rgb(var(--muted))' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'rgb(var(--surface))',
                      border: '1px solid rgb(var(--border))',
                      borderRadius: 12,
                      boxShadow: '0 20px 50px -20px rgb(0 0 0 / 0.5)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 11,
                    }}
                    labelStyle={{ color: 'rgb(var(--text))', fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    stroke="url(#sClicks)"
                    strokeWidth={2.5}
                    fill="url(#gClicks)"
                    name={t('Clicks')}
                  />
                  <Area
                    type="monotone"
                    dataKey="conversions"
                    stroke="rgb(var(--success))"
                    strokeWidth={2}
                    fill="url(#gConv)"
                    name={t('Conversions')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-muted-foreground" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                {t('No performance data yet')}
              </div>
            )}
          </CardContent>
        </Card>
        <LiveClickFeed campaigns={allCampaigns} sources={sources} />
      </div>

      {/* Campaigns: a compact table, like the site mock */}
      <div className="xp-dash-row">
        <div className="xp-panel xp-dash-campaigns">
          <div className="xp-panel-h3 xp-dash-h">
            <h3 className="xp-panel-title">
              <Target className="w-4 h-4" />
              {t('Recent campaigns')}
              <span className="xp-panel-sub">{periodLabel || t('All time')}</span>
            </h3>
            <Link to="/campaigns" className="lcf-link">
              {t('Show all campaigns')} →
            </Link>
          </div>
          {loading ? (
            <div className="lcf-body">
              <div className="lcf-skel" aria-hidden="true">
                <i /><i /><i />
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="xp-dash-empty">
              <Target className="w-8 h-8" />
              <p>{t('You have no campaigns yet')}</p>
              <Link to="/campaigns" className="xp-btn xp-btn-primary">
                {t('Create your first campaign')}
              </Link>
            </div>
          ) : (
            <div className="xp-ctable-wrap">
              <table className="xp-ctable">
                <thead>
                  <tr>
                    <th>{t('Campaign')}</th>
                    <th className="num">{t('Clicks')}</th>
                    <th className="num">{t('Conv.')}</th>
                    <th className="num">{t('CR')}</th>
                    <th className="num hide-lg">{t('Revenue')}</th>
                    <th className="num hide-lg">{t('Cost')}</th>
                    <th className="num hide-md">{t('Profit')}</th>
                    <th className="num">{t('ROI')}</th>
                    <th className="num hide-xl">{t('EPC')}</th>
                    <th className="xp-spark-col" title={t('Clicks, last 14 days')}>{t('Trend')}</th>
                    <th className="c-go" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const sum = summary[campaign.id];
                    const clicks = Number(sum ? sum.clicks : campaign.total_clicks) || 0;
                    const conv = Number(sum ? sum.conversions : campaign.conversions) || 0;
                    const revenue = Number(sum ? sum.revenue : campaign.revenue) || 0;
                    const cost = Number(sum && sum.cost) || 0;
                    const profit = revenue - cost;
                    const cr = clicks > 0 ? ((conv / clicks) * 100).toFixed(1) + '%' : '—';
                    const roi = cost > 0 ? (profit / cost) * 100 : null;
                    const epc = clicks > 0 ? revenue / clicks : null;
                    const cur = (sum && sum.currency) || 'USD';
                    const source = sources[campaign.traffic_source_id];
                    return (
                      <tr
                        key={campaign.id}
                        onClick={() => navigate('/campaigns/' + campaign.id)}
                        data-testid={'campaign-item-' + campaign.id}
                      >
                        <td>
                          <div className="c-name">
                            <span
                              className={'c-dot' + (campaign.is_active ? ' on' : '')}
                              title={campaign.is_active ? t('Active') : t('Paused')}
                            />
                            <Link to={'/campaigns/' + campaign.id} onClick={(ev) => ev.stopPropagation()}>
                              {campaign.name}
                            </Link>
                            {source ? <span className="c-src">{source}</span> : null}
                          </div>
                        </td>
                        <td className="num">{clicks.toLocaleString()}</td>
                        <td className="num">{conv.toLocaleString()}</td>
                        <td className="num">{cr}</td>
                        <td className={'num hide-lg' + (revenue ? '' : ' dim')}>{money(revenue, cur)}</td>
                        <td className={'num hide-lg' + (cost ? '' : ' dim')}>{money(cost, cur)}</td>
                        <td className={'num hide-md' + (profit > 0 ? ' pos' : profit < 0 ? ' neg' : ' dim')}>{money(profit, cur)}</td>
                        <td className={'num' + (roi == null ? ' dim' : roi >= 0 ? ' pos' : ' neg')}>
                          {roi == null ? '—' : (roi >= 0 ? '+' : '') + roi.toFixed(0) + '%'}
                        </td>
                        <td className={'num hide-xl' + (epc ? '' : ' dim')}>{epc == null ? '—' : money(epc, cur, 3)}</td>
                        <td className="xp-spark-col">
                          <Spark data={trends[campaign.id]} />
                        </td>
                        <td className="c-go">
                          <ArrowUpRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
